import React, { ComponentType } from 'react';
import {
  command,
  CommandFunction,
  NodeSpecOverride,
  ProsemirrorAttributes,
} from 'remirror';
import {
  ApplySchemaAttributes,
  extension,
  ExtensionTag,
  getMarkRange,
  NodeExtension,
  NodeExtensionSpec,
  removeNodeAtPosition,
} from '@remirror/core';
import { NodeViewComponentProps } from '@remirror/extension-react-component';
import LinkCard from './LinkCard';
import { APIResponse } from 'utils/urlInfo';

interface LinkCardOptions {}

@extension<LinkCardOptions>({
  defaultOptions: {},
  staticKeys: [],
})
export class LinkCardExtension extends NodeExtension<LinkCardOptions> {
  get name() {
    return 'linkCard' as const;
  }

  createTags() {
    return [ExtensionTag.Block];
  }

  createNodeSpec(
    extra: ApplySchemaAttributes,
    override: NodeSpecOverride,
  ): NodeExtensionSpec {
    return {
      attrs: {
        ...extra.defaults(),
        url: { default: '' },
        metadata: { default: null },
      },
      selectable: true,
      draggable: false,
      atom: true,
      isolating: true,
      content: 'block*',
      ...override,
      toDOM: (node) => {
        const attributes = {
          ...extra.dom(node),
        };
        return ['div', 0];
      },
    };
  }

  @command()
  insertLinkCard(attributes: LinkCardAttributes): CommandFunction {
    return ({ tr, dispatch }) => {
      dispatch?.(
        tr
          .replaceWith(
            attributes.selection || tr.selection.from,
            (attributes.selection || tr.selection.from) + 1,
            this.type.create(attributes),
          )
          .scrollIntoView(),
      );
      return true;
    };
  }

  @command()
  setLinkCardUrl(pos: number, url: string): CommandFunction {
    return ({ tr, state, dispatch }) => {
      const node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        dispatch?.(
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            url,
            metadata: null,
          }),
        );
        return true;
      }

      return false;
    };
  }

  @command()
  setLinkCardMetadata(pos: number, metadata: APIResponse): CommandFunction {
    return ({ tr, state, dispatch }) => {
      const node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        dispatch?.(
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            metadata,
          }),
        );
        return true;
      }

      return false;
    };
  }

  @command()
  deleteLinkCard(pos: number): CommandFunction {
    return ({ tr, state, dispatch }) => {
      const node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        dispatch?.(removeNodeAtPosition({ pos, tr }));
        return true;
      }

      return false;
    };
  }

  @command()
  replaceLinkWithLinkCard(pos: number, url: string): CommandFunction {
    return ({ tr, state, dispatch }) => {
      const $pos = tr.doc.resolve(pos);
      const range = getMarkRange($pos, 'link');
      if (!range?.mark) {
        return false;
      }

      removeNodeAtPosition({ pos: range.from, tr });

      this.insertLinkCard({
        url: range.mark.attrs.href,
        selection: range?.from,
      })({ tr, state, dispatch });

      return true;
    };
  }

  ReactComponent: ComponentType<NodeViewComponentProps> = (props) => {
    return <LinkCard {...props} />;
  };
}

export type LinkCardAttributes =
  ProsemirrorAttributes<LinkCardExtensionAttributes>;

export interface LinkCardExtensionAttributes {
  selection?: number;
  url?: string;
}

declare global {
  namespace Remirror {
    interface AllExtensions {
      linkCard: LinkCardExtension;
    }
  }
}
